/**
 * Note Process
 * 
 * Simple editable text box that AI can populate and users can edit.
 * 
 * **Layout Anatomy:**
 * - Single view with an editable text area
 * - AI can populate the content
 * - Users can edit the content
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: STOP_ON_ALL_VIEWS_CLOSE
 * - Views: 1 view (single editable text box)
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { cortexBridge } from '../../services/cortex-bridge/service.js';

interface NoteState {
  content: string;
  title?: string;
}

export class NoteProcess extends BaseProcess {
  kind: string = 'note';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: NoteState = {
    content: '',
    title: undefined
  };

  private noteView: NoteView | null = null;
  private updateTimer: number | null = null;

  constructor(id: string) {
    super(id);
    
    // Listen for remote updates
    bus.on('note.updated.remote', (note: Partial<NoteState> & { id: string }) => {
      if (note.id === this.id) {
        this.state = { ...this.state, ...note };
        this.updateState(this.state);
      }
    });
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[NoteProcess] onStart called for process ${this.id}`);
    
    // Create single view with editable text box
    const view = new NoteView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.noteView = view;
    
    log.debug(`[NoteProcess] Registering view: ${viewInstance.id}`);
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);

    // Emit process.started
    log.debug(`[NoteProcess] View registered, emitting process.started`);
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  updateState(newState: NoteState): void {
    this.state = newState;
    // Update the view
    if (this.noteView) {
      this.noteView.updateState(this.state);
    }
  }

  /**
   * Update note content (debounced sync to Cortex)
   */
  updateNote(updates: Partial<NoteState>): void {
    this.state = { ...this.state, ...updates };
    this.updateState(this.state); // Optimistic update

    // Clear existing timer
    if (this.updateTimer) {
      window.clearTimeout(this.updateTimer);
    }

    // Debounce sendIntent (1s)
    this.updateTimer = window.setTimeout(() => {
      cortexBridge.sendIntent('UPDATE_NOTE', { 
        id: this.id, 
        ...updates 
      });
    }, 1000);
  }
}

/**
 * Note View (HTML-based)
 * 
 * Renders an editable text box.
 */
class NoteView extends BaseViewHTML {
  private textarea: HTMLTextAreaElement | null = null;

  constructor(
    processId: string,
    viewId: string,
    private state: NoteState,
    private process: NoteProcess
  ) {
    super(processId, viewId, {
      acceptableSizes: [
        { cols: 3, rows: 3 }, // Preferred: 3x3 for comfortable editing
        { cols: 2, rows: 2 },  // Minimum: 2x2
        { cols: 4, rows: 4 }   // Larger option
      ],
      showDismissButton: true,
      priority: 100
    });
  }

  protected getContentHTML(): string {
    return `
      <div style="
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 16px;
        box-sizing: border-box;
      ">
        <input 
          type="text" 
          class="note-title" 
          value="${this.escapeHtml(this.state.title || '')}" 
          placeholder="Title"
          style="
            width: 100%;
            padding: 8px;
            margin-bottom: 12px;
            background: rgba(102, 204, 255, 0.1);
            border: 1px solid rgba(102, 204, 255, 0.3);
            border-radius: 4px;
            color: var(--ink, #cfd3dc);
            font-size: 14px;
            font-weight: 600;
            box-sizing: border-box;
          "
        />
        <textarea 
          class="note-content"
          placeholder="Type your note here..."
          style="
            width: 100%;
            flex: 1;
            padding: 12px;
            background: rgba(102, 204, 255, 0.05);
            border: 1px solid rgba(102, 204, 255, 0.2);
            border-radius: 4px;
            color: var(--ink, #cfd3dc);
            font-size: 13px;
            font-family: inherit;
            line-height: 1.5;
            resize: none;
            box-sizing: border-box;
          "
        >${this.escapeHtml(this.state.content)}</textarea>
      </div>
    `;
  }

  protected render(): void {
    // Call parent to set base styles
    super.render();
    
    if (!this.element) return;
    
    // Setup event listeners for editing
    this.textarea = this.element.querySelector('.note-content') as HTMLTextAreaElement;
    const titleInput = this.element.querySelector('.note-title') as HTMLInputElement;
    
    if (this.textarea) {
      // Update state when user edits
      this.textarea.addEventListener('input', () => {
        if (this.textarea) {
          this.process.updateNote({ content: this.textarea.value });
          
          // Emit update event so AI/system can track changes
          bus.emit('note.updated', {
            processId: this.processId,
            viewId: this.id,
            content: this.textarea.value
          });
        }
      });
      
      // Set initial value
      this.textarea.value = this.state.content;
    }
    
    if (titleInput) {
      titleInput.addEventListener('input', () => {
        this.process.updateNote({ title: titleInput.value });
        
        bus.emit('note.updated', {
          processId: this.processId,
          viewId: this.id,
          title: titleInput.value,
          content: this.state.content
        });
      });
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  updateState(newState: NoteState): void {
    log.debug(`[NoteView] updateState() called for ${this.id}`);
    this.state = newState;
    
    // Update textarea if it exists
    if (this.textarea) {
      this.textarea.value = this.state.content;
    }
    
    // Update title input if it exists
    const titleInput = this.element?.querySelector('.note-title') as HTMLInputElement;
    if (titleInput) {
      titleInput.value = this.state.title || '';
    }
    
    this.markDirty();
  }
}

registerProcess('note', (id: string) => new NoteProcess(id));

